import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Select from "react-select";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import WBCModal from "../elements/WBCModal";
import WBCPrimaryOutlinedButton from "../elements/WBCPrimaryOutlinedButton";
import CustomExportApi from "../../httpClients/CustomExportApi";
import FileHelper from "../../httpClients/FileHelper";
import {
  BodyContent,
  FooterContent,
  SelectedColumnsContainer,
  SelectedItem,
  DragHandle,
  RemoveButton,
  AddAllLink
} from "./CustomDownloadStyles";

const CustomDownloadModal = ({ fedTeam, rosterType, isOpen, onClose, rosterName, federation, profileIds }) => {
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [columnOptions, setColumnOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    CustomExportApi.getColumnsByRoster(rosterType)
      .then(res => {
        const options = res.map(col => ({
          value: col.id.toString(),
          label: col.columnLabel
        }));
        setColumnOptions(options);
      })
      .catch(error => {
        console.error("Error fetching columns:", error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [rosterType, isOpen]);

  const handleDownload = () => {
    const columnIds = selectedColumns.map(col => col.value);
    setIsDownloading(true);
    CustomExportApi.getProfileExport(fedTeam, rosterType, columnIds, profileIds)
      .then(response => {
        const url = window.URL.createObjectURL(response);
        const downloadHeaders = {
          "content-disposition": `attachment; filename="${federation || ""} - ${rosterName || "roster"}-export.xlsx"`,
          "content-type": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        };
        FileHelper.downloadFile(url, downloadHeaders);
        window.URL.revokeObjectURL(url);
        onClose();
      })
      .catch(error => {
        console.error("Download failed:", error);
      })
      .finally(() => {
        setIsDownloading(false);
      });
  };

  const selectStyles = {
    menuPortal: provided => ({
      ...provided,
      zIndex: 9999
    }),
    menu: provided => ({
      ...provided,
      zIndex: 9999
    })
  };

  const handleDragEnd = result => {
    if (!result.destination) return;
    if (result.destination.index === result.source.index) return;

    const items = Array.from(selectedColumns);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    setSelectedColumns(items);
  };

  const removeColumn = columnToRemove => {
    setSelectedColumns(prev => prev.filter(col => col.value !== columnToRemove.value));
  };

  const addAllColumns = () => {
    setSelectedColumns([...columnOptions]);
  };

  const removeAllColumns = () => {
    setSelectedColumns([]);
  };

  const availableOptions = columnOptions.filter(
    option => !selectedColumns.some(selected => selected.value === option.value)
  );

  return (
    <WBCModal isOpen={isOpen} toggleModal={onClose} headerText="Customize Export" size={"md"}>
      <BodyContent>
        <div style={{ marginBottom: "1rem" }}>
          <div style={{ marginBottom: "0.5rem" }}>
            <Select
              value={null}
              options={availableOptions}
              onChange={selectedOptions => {
                if (selectedOptions && selectedOptions.length > 0) {
                  setSelectedColumns(prev => [...prev, ...selectedOptions]);
                }
              }}
              placeholder={isLoading ? "Loading columns..." : "Choose columns to add..."}
              isLoading={isLoading}
              isDisabled={isLoading}
              styles={selectStyles}
              menuPortalTarget={document.body}
              isMulti
              closeMenuOnSelect={false}
              hideSelectedOptions={true}
              isClearable={true}
            />
          </div>
          {!isLoading && (
            <div>
              {availableOptions.length > 0 && <AddAllLink onClick={addAllColumns}>Add all columns</AddAllLink>}
              {selectedColumns.length > 0 && <AddAllLink onClick={removeAllColumns}>Remove All Columns</AddAllLink>}
            </div>
          )}
        </div>

        {selectedColumns.length > 0 && (
          <div>
            <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: "bold" }}>
              Selected Columns (drag to reorder):
            </label>
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="columns">
                {provided => (
                  <SelectedColumnsContainer {...provided.droppableProps} ref={provided.innerRef}>
                    {selectedColumns.map((column, index) => (
                      <Draggable key={`column-${column.value}`} draggableId={`column-${column.value}`} index={index}>
                        {(provided, snapshot) => (
                          <SelectedItem
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            className={snapshot.isDragging ? "dragging" : ""}
                          >
                            <div style={{ display: "flex", alignItems: "center" }}>
                              <DragHandle>⋮⋮</DragHandle>
                              <span>{column.label}</span>
                            </div>
                            <RemoveButton
                              onClick={e => {
                                e.stopPropagation();
                                e.preventDefault();
                                removeColumn(column);
                              }}
                            >
                              x
                            </RemoveButton>
                          </SelectedItem>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </SelectedColumnsContainer>
                )}
              </Droppable>
            </DragDropContext>
          </div>
        )}
      </BodyContent>
      <FooterContent>
        <WBCPrimaryOutlinedButton
          onClick={handleDownload}
          disabled={selectedColumns?.length === 0 || isLoading || isDownloading}
        >
          {isDownloading ? "Downloading..." : "Download"}
        </WBCPrimaryOutlinedButton>
        <WBCPrimaryOutlinedButton onClick={onClose}>Cancel</WBCPrimaryOutlinedButton>
      </FooterContent>
    </WBCModal>
  );
};

CustomDownloadModal.propTypes = {
  fedTeam: PropTypes.object.isRequired,
  rosterType: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  rosterName: PropTypes.string,
  federation: PropTypes.string,
  profileIds: PropTypes.array
};

export default CustomDownloadModal;
